import { ConfirmModal } from '@vrplatform/components';
import {
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { useReservationDetailDrawer } from '../useReservationDetailDrawer';
import { showWarnNotification } from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';

type Props = {
  reservationId: Maybe<string>;
  opened: boolean;
  closeModal: () => void;
};

export const ReservationDeleteModal = ({
  opened,
  closeModal,
  reservationId,
}: Props) => {
  const { close: closeDrawer } = useReservationDetailDrawer();

  const invalidate = useInvalidateApiQueries(['reservations']);
  const { mutateAsync, isPending: loading } = useApiMutation(
    'delete',
    '/reservations/{id}'
  );
  return (
    <ConfirmModal
      type="delete"
      opened={opened}
      closeModal={closeModal}
      loading={loading}
      title={'Are you sure you want to delete the reservation?'}
      subtitle="This will delete any related journal entries."
      onSubmit={async () => {
        if (!reservationId) {
          captureSentryError('Missing reservation to delete');
          showWarnNotification({
            title: 'Missing reservation',
            message: 'Please reach out to support if the issue persists.',
          });
          return;
        }

        await mutateAsync({
          params: {
            path: {
              id: reservationId,
            },
            query: {
              onLocked: 'error',
            },
          },
        })
          .then(() => {
            invalidate();
            closeModal();
            closeDrawer();
          })
          .catch((error) => {
            showApiErrorNotification({
              error,
              title: 'Failed to delete reservation',
              defaultMessage:
                'We failed to delete the reservation. Please reach out to support if the issue persists.',
            });
          });
      }}
    />
  );
};
